import { useCallback, useRef, useState } from "react";
import { useAppDispatch } from "hooks";
import getRideHistoryAction from "domain/rides/store/actions/get-ride-history.action";
import {
  RideHistoryItemDto,
  RideHistoryRequestDto
} from "domain/rides/entities/dtos/ride-history.dto";

/**
 * Owns the ride history modal: every open fetches the history again, and a
 * failed request closes the modal (the action has already shown the toast).
 * Nothing is kept in Redux — the items live only while the modal is open.
 */
export const useRideHistory = ({
  accountId,
  objectId,
  rideId
}: RideHistoryRequestDto) => {
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<RideHistoryItemDto[]>([]);

  // Bumped on every open and close, so a response that arrives after the
  // modal was closed (or reopened) cannot overwrite the current state.
  const requestId = useRef(0);

  const openHistory = useCallback(async () => {
    const currentRequest = ++requestId.current;

    setOpen(true);
    setLoading(true);
    setItems([]);

    const result = await dispatch(
      getRideHistoryAction({ accountId, objectId, rideId })
    );

    if (currentRequest !== requestId.current) return;

    if (getRideHistoryAction.fulfilled.match(result)) {
      setItems(result.payload?.items ?? []);
    } else {
      setOpen(false);
    }
    setLoading(false);
  }, [dispatch, accountId, objectId, rideId]);

  const closeHistory = useCallback(() => {
    requestId.current++;
    setOpen(false);
    setLoading(false);
  }, []);

  return { open, loading, items, openHistory, closeHistory };
};

export default useRideHistory;
